<template>
  <!--
    Editor de documentos sobre el CORE open-source de GrapesJS (paquete `grapesjs`,
    BSD-3; NO el Studio SDK propietario). Única fuente de verdad de la init de
    GrapesJS para la feature "doc builder": encapsula el lienzo A4 y expone métodos
    (setPageSize / importHtml / getHtml / getCss / applyHtml / applyCss /
    applyComponentHtml / getProjectData / loadProjectData / exportHtmlCss / printA4)
    vía $refs para que la vista demo solo orqueste el sidebar. Emite el evento
    `edit-component-code` (con el HTML del componente seleccionado) cuando el usuario
    pulsa el botón "</>" de la toolbar de un componente.
  -->
  <div class="grapes-doc-editor">
    <!-- GrapesJS monta su DOM (paneles, bloques, iframe del canvas) aquí. -->
    <div ref="gjs" class="grapes-doc-editor__canvas"></div>
  </div>
</template>

<script>
// --- Imports del CORE OSS de GrapesJS (grupo "external", import/order) ---
// `grapesjs` es el core BSD-3; NO se importa @grapesjs/studio-sdk (propietario).
// El alias `grapesjs$` en vue.config.js fuerza el build CommonJS (dist/grapes.min.js):
// el package.json de grapesjs declara `module: dist/grapes.mjs`, que webpack 4
// (@vue/cli-service 4.5) prioriza y no procesa en runtime
// (TypeError: __webpack_require__.r is not a function).
import grapesjs from 'grapesjs';
// CSS base del editor (paneles, bloques, selectores). Es CSS GLOBAL del core:
// ojo con la interacción con purgecss/tailwind del repo (ver notas del spike).
import 'grapesjs/dist/css/grapes.min.css';

// Contenido ejemplo tipo "factura/informe" precargado en el lienzo. Vive en un
// template literal (exento de la regla `indent` por `ignoredNodes:['TemplateLiteral']`).
const SAMPLE_INVOICE_HTML = `
  <div class="doc-page">
    <h1 class="doc-title">FACTURA ELECTRÓNICA</h1>
    <p class="doc-meta">Folio N° 0001 — Fecha: 01/01/2025</p>
    <table class="doc-table">
      <thead>
        <tr><th>Detalle</th><th>Cantidad</th><th>Precio</th><th>Total</th></tr>
      </thead>
      <tbody>
        <tr><td>Servicio de ejemplo</td><td>1</td><td>$10.000</td><td>$10.000</td></tr>
        <tr><td>Otro ítem</td><td>2</td><td>$5.000</td><td>$10.000</td></tr>
      </tbody>
    </table>
    <p class="doc-total">Total: $20.000</p>
  </div>
`;

// Mapa de tamaños de página soportados. `width`/`height` dimensionan la hoja
// (.doc-page) en el visor; `print` es el valor de la at-rule de página para la
// impresión real. A4 = 210x297mm; CARTA (Letter) = 216x279mm.
const PAGE_SIZES = {
  A4: { width: '210mm', height: '297mm', print: 'A4' },
  CARTA: { width: '216mm', height: '279mm', print: 'letter' },
};

// Devuelve el tamaño válido o cae a A4 si la clave no existe.
function resolveSize(sizeKey) {
  return PAGE_SIZES[sizeKey] ? sizeKey : 'A4';
}

// Construye los estilos del documento + hoja parametrizados por tamaño. Se
// inyectan en el CANVAS del editor (iframe interno) vía setStyle/addStyle. Con el
// core NO existe un preset imprimible (presetPrintable es del Studio SDK); aquí lo
// construimos a mano con CSS. La hoja ES .doc-page: el tamaño va en su width y
// min-height, NUNCA en el body (eso provocaba el bug de una hoja dentro de otra).
// Incluye además los estilos de seccion, filas y columnas usados por los bloques.
function buildDocCss(sizeKey) {
  const size = PAGE_SIZES[resolveSize(sizeKey)];
  return `
    body { background: #e9e9e9; margin: 0; }
    .doc-page {
      width: ${size.width};
      min-height: ${size.height};
      margin: 10mm auto;
      padding: 20mm;
      background: #ffffff;
      box-shadow: 0 0 8px rgba(0,0,0,0.15);
      box-sizing: border-box;
      font-family: Arial, Helvetica, sans-serif;
      color: #222;
    }
    .doc-title { font-size: 20px; margin: 0 0 4px; }
    .doc-meta { font-size: 12px; color: #666; margin: 0 0 16px; }
    .doc-table { width: 100%; border-collapse: collapse; font-size: 12px; }
    .doc-table th, .doc-table td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
    .doc-table th { background: #f3f3f3; }
    .doc-total { text-align: right; font-weight: bold; margin-top: 16px; }
    .doc-section { margin: 0 0 16px; }
    .doc-row { display: flex; gap: 12px; margin: 0 0 16px; }
    .doc-col { flex: 1 1 0; min-height: 24px; border: 1px dashed #e0e0e0; padding: 4px; box-sizing: border-box; }
    .doc-col--1-2 { flex: 0 0 50%; }
    .doc-col--1-3 { flex: 0 0 33.333%; }
    .doc-col--2-3 { flex: 0 0 66.666%; }
  `;
}

// Reglas de página / media print para que la impresión salga en el tamaño activo,
// sin la UI del editor. Se aplican en la ventana aislada de impresión (printA4).
function buildPrintCss(sizeKey) {
  const size = PAGE_SIZES[resolveSize(sizeKey)];
  return `
    @page { size: ${size.print}; margin: 0; }
    @media print {
      html, body { margin: 0; padding: 0; background: #ffffff; }
      .doc-page { margin: 0; box-shadow: none; width: ${size.width}; min-height: ${size.height}; }
    }
  `;
}

// Bloques del documento registrados en el BlockManager del core (categoría
// "Documento"). El core no trae bloques por defecto. El contenido de cada bloque
// vive DENTRO de .doc-page; solo el bloque Página crea una nueva hoja.
const DOC_BLOCKS = [
  {
    id: 'doc-pagina',
    label: 'Página',
    category: 'Documento',
    content: '<div class="doc-page"></div>',
  },
  {
    id: 'doc-seccion',
    label: 'Sección',
    category: 'Documento',
    content: '<section class="doc-section"></section>',
  },
  {
    id: 'doc-titulo',
    label: 'Título',
    category: 'Documento',
    content: '<h1 class="doc-title">Título</h1>',
  },
  {
    id: 'doc-subtitulo',
    label: 'Subtítulo',
    category: 'Documento',
    content: '<h2 class="doc-title">Subtítulo</h2>',
  },
  {
    id: 'doc-texto',
    label: 'Texto',
    category: 'Documento',
    content: '<p>Escribe aquí tu texto.</p>',
  },
  {
    id: 'doc-tabla',
    label: 'Tabla',
    category: 'Documento',
    content: `
      <table class="doc-table">
        <thead>
          <tr><th>Columna 1</th><th>Columna 2</th><th>Columna 3</th></tr>
        </thead>
        <tbody>
          <tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>
          <tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>
        </tbody>
      </table>
    `,
  },
  {
    id: 'doc-col-1-2',
    label: 'Columnas 1/2 + 1/2',
    category: 'Documento',
    content: '<div class="doc-row"><div class="doc-col doc-col--1-2"></div><div class="doc-col doc-col--1-2"></div></div>',
  },
  {
    id: 'doc-col-1-3-2-3',
    label: 'Columnas 1/3 + 2/3',
    category: 'Documento',
    content: '<div class="doc-row"><div class="doc-col doc-col--1-3"></div><div class="doc-col doc-col--2-3"></div></div>',
  },
  {
    id: 'doc-col-2-3-1-3',
    label: 'Columnas 2/3 + 1/3',
    category: 'Documento',
    content: '<div class="doc-row"><div class="doc-col doc-col--2-3"></div><div class="doc-col doc-col--1-3"></div></div>',
  },
  {
    id: 'doc-col-1-3-1-3-1-3',
    label: 'Columnas 1/3 + 1/3 + 1/3',
    category: 'Documento',
    content: '<div class="doc-row"><div class="doc-col doc-col--1-3"></div><div class="doc-col doc-col--1-3"></div><div class="doc-col doc-col--1-3"></div></div>',
  },
  {
    id: 'doc-separador',
    label: 'Separador',
    category: 'Documento',
    content: '<hr />',
  },
  {
    id: 'doc-imagen',
    label: 'Imagen',
    category: 'Documento',
    content: { type: 'image' },
  },
];

export default {
  name: 'GrapesDocEditor',

  props: {
    // Tamaño de página inicial del documento: 'A4' (default) o 'CARTA'.
    initialPageSize: {
      type: String,
      default: 'A4',
    },
  },

  data() {
    return {
      editor: null,
      // Tamaño activo del documento; se valida contra PAGE_SIZES (fallback A4).
      currentSize: resolveSize(this.initialPageSize),
    };
  },

  mounted() {
    // Monta sobre un $ref; GrapesJS crea DOM e iframe FUERA del árbol reactivo
    // de Vue, por eso se limpia en beforeDestroy() con editor.destroy().
    this.editor = grapesjs.init({
      container: this.$refs.gjs,
      height: '100%',
      width: 'auto',
      fromElement: false,
      // Sin backend: round-trip JSON manual con getProjectData/loadProjectData.
      // `store()`/`load()` requerirían un storageManager con backend real.
      storageManager: { type: 'none' },
      // Un documento es de tamaño fijo (A4/Carta), no responsive: dejamos un único
      // device "Desktop" para que el selector no ofrezca Tablet/Mobile.
      deviceManager: {
        devices: [{ name: 'Desktop', width: '' }],
      },
      // Bloques básicos del documento (el core no trae bloques por defecto).
      blockManager: { blocks: DOC_BLOCKS },
    });

    // Precarga del contenido ejemplo tipo factura + estilos de hoja del documento
    // en el tamaño inicial.
    this.editor.setComponents(SAMPLE_INVOICE_HTML);
    this.editor.setStyle(buildDocCss(this.currentSize));

    // Comando que la toolbar de cada componente invoca para editar su HTML crudo.
    // No abre UI aquí: emite un evento Vue para que la vista muestre el editor de
    // código (patrón "Editor online" del repo), y recibe de vuelta el HTML editado.
    this.editor.Commands.add('doc:edit-component-code', {
      run: (ed) => {
        const selected = ed.getSelected();
        if (!selected) return;
        this.$emit('edit-component-code', selected.toHTML());
      },
    });

    // Agrega el botón "</>" (Editar HTML) a la toolbar de CADA componente, salvo
    // wrapper/body. Se hace al seleccionar, para cubrir también lo importado.
    this.editor.on('component:selected', (component) => {
      const toolbar = component.get('toolbar') || [];
      const already = toolbar.some((t) => t.command === 'doc:edit-component-code');
      if (already) return;
      component.set('toolbar', [
        ...toolbar,
        {
          attributes: { class: 'fa fa-code', title: 'Editar HTML' },
          command: 'doc:edit-component-code',
        },
      ]);
    });
  },

  beforeDestroy() {
    // Imprescindible en Vue 2: libera DOM/iframe/listeners creados por GrapesJS.
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  },

  methods: {
    // Cambia el tamaño de página en caliente (A4/CARTA). Usa addStyle (fusiona)
    // en vez de setStyle (reemplaza) para no borrar los estilos del documento;
    // solo re-escribe las reglas de hoja. Ignora claves no soportadas.
    setPageSize(sizeKey) {
      if (!this.editor || !PAGE_SIZES[sizeKey]) return;
      this.currentSize = sizeKey;
      this.editor.addStyle(buildDocCss(sizeKey));
    },

    // Importa HTML en el lienzo, controlando qué pasa con el CSS. `setComponents`
    // REEMPLAZA el contenido del documento y, de paso, incorpora al proyecto los
    // estilos (<style>/inline) que trae el HTML. El parámetro `cssMode` decide el
    // destino del CSS:
    //   - 'merge'   (fusionar): CSS previo del proyecto + estilos del HTML importado.
    //   - 'replace' (reemplazar): descarta el CSS previo; deja solo el del HTML.
    //   - 'keep'    (mantener): conserva el CSS previo; descarta el del HTML importado.
    importHtml(html, cssMode = 'merge') {
      if (!this.editor || !html) return;

      // CSS del proyecto ANTES de importar (para los modos keep/replace).
      const previousCss = this.editor.getCss();

      if (cssMode === 'replace') {
        // Vacía el CSS del proyecto; setComponents dejará solo el del HTML importado.
        this.editor.setStyle('');
      }

      // Trae componentes del HTML (y fusiona sus estilos en el CSS del proyecto).
      this.editor.setComponents(html);

      if (cssMode === 'keep') {
        // Restaura el CSS previo, descartando los estilos que trajo el HTML.
        this.editor.setStyle(previousCss);
      }
    },

    // Reemplaza el HTML del componente actualmente seleccionado por el editado en
    // el editor de código por-componente. replaceWith intercambia el componente en
    // el árbol; GrapesJS reparsea el HTML a su modelo editable.
    applyComponentHtml(html) {
      if (!this.editor || html == null) return;
      const selected = this.editor.getSelected();
      if (!selected) return;
      selected.replaceWith(html);
    },

    // HTML global del documento (sin CSS), para la pestaña HTML del editor global.
    getHtml() {
      return this.editor ? this.editor.getHtml() : '';
    },

    // CSS global del documento, para la pestaña CSS del editor global.
    getCss() {
      return this.editor ? this.editor.getCss() : '';
    },

    // Aplica el HTML global editado (pestaña HTML). setComponents reemplaza todo el
    // contenido del documento. Tras ello se reaplica la hoja del tamaño activo con
    // addStyle para no perder el aspecto de página.
    applyHtml(html) {
      if (!this.editor || html == null) return;
      this.editor.setComponents(html);
      this.editor.addStyle(buildDocCss(this.currentSize));
    },

    // Aplica el CSS global editado (pestaña CSS). setStyle reemplaza el CSS del
    // proyecto; luego se reaplica la hoja del tamaño activo (addStyle fusiona) para
    // garantizar que las reglas de .doc-page sigan presentes.
    applyCss(css) {
      if (!this.editor || css == null) return;
      this.editor.setStyle(css);
      this.editor.addStyle(buildDocCss(this.currentSize));
    },

    // JSON del proyecto (pages/components/styles) independiente del storageManager.
    getProjectData() {
      if (!this.editor) return null;
      return this.editor.getProjectData();
    },

    // Carga un JSON de proyecto previamente obtenido con getProjectData. Round-trip
    // puro en memoria, sin backend.
    loadProjectData(json) {
      if (!this.editor || !json) return;
      this.editor.loadProjectData(json);
    },

    // HTML + CSS exportados del documento (API de core: getHtml/getCss).
    exportHtmlCss() {
      if (!this.editor) return { html: '', css: '' };
      return {
        html: this.editor.getHtml(),
        css: this.editor.getCss(),
      };
    },

    // Impresión/Preview en el tamaño activo. El core NO trae preset imprimible
    // (presetPrintable es del Studio SDK). Tomamos el HTML+CSS exportado y lo
    // abrimos en una ventana aislada con CSS de página / media print, para
    // imprimir SOLO el documento en el tamaño actual (A4/CARTA).
    printA4() {
      if (!this.editor) return;
      const { html, css } = this.exportHtmlCss();
      const win = window.open('', '_blank');
      if (!win) return;
      win.document.open();
      win.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Documento</title>
            <style>${css}</style>
            <style>${buildPrintCss(this.currentSize)}</style>
          </head>
          <body>${html}</body>
        </html>
      `);
      win.document.close();
      win.focus();
      // Pequeño delay para asegurar layout antes de imprimir.
      win.setTimeout(() => win.print(), 250);
    },
  },
};
</script>

<style scoped>
.grapes-doc-editor {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.grapes-doc-editor__canvas {
  width: 100%;
  height: 100%;
  overflow: auto;
}
</style>
